import { Box, useTheme } from '@mui/material';
import type { ReactNode } from 'react';

import {
  SIDE_PANEL_DEFAULT_OVERLAY_BREAKPOINT_PX,
  SIDE_PANEL_DEFAULT_WIDTH,
} from '../constants';

/**
 * Props for the generic `SidePanelLayout` wrapper.
 *
 * Consumers slot in their main column content and a pre-composed panel
 * node (including any error boundary they want around it). The layout
 * owns flex sizing and the push/overlay margin rule; it does not own
 * panel mounting, visibility gating, or feature-flag checks.
 */
export type SidePanelLayoutProps = {
  /** The primary content column (page body, tabs, etc.). */
  mainContent: ReactNode;
  /**
   * The side panel slot. Typically a `SidePanel` or a branded wrapper
   * around it. Rendered as a sibling of `mainContent` so z-index and
   * error boundaries can be scoped to it without affecting layout.
   */
  panel: ReactNode;
  /**
   * Whether the side panel is currently open. Drives the persistent
   * main-column `margin-right` push. When the viewport is below
   * `overlayBelowPx`, this prop has no layout effect (the panel
   * overlays with a scrim instead).
   */
  isPanelOpen: boolean;
  /**
   * Fixed panel width (px). Must match the `width` prop passed to the
   * paired `SidePanel` so the main column yields the exact right-edge
   * the panel occupies.
   * @default SIDE_PANEL_DEFAULT_WIDTH
   */
  panelWidth?: number;
  /**
   * Viewport width (px) at which the layout flips from overlay (no
   * push) to persistent push. Must match the `overlayBelowPx` passed
   * to the paired `SidePanel`.
   * @default SIDE_PANEL_DEFAULT_OVERLAY_BREAKPOINT_PX
   */
  overlayBelowPx?: number;
  /** `data-testid` forwarded onto the outer flex-row wrapper. */
  rootTestId?: string;
  /** `data-testid` forwarded onto the inner main-column wrapper. */
  mainColumnTestId?: string;
};

/**
 * Flex-row layout that hosts a main content column beside a right-side
 * panel. Owns the responsive push-vs-overlay transition so consumers
 * do not have to re-implement the media-query + transition dance.
 *
 * Layout contract:
 * - Outer wrapper: `flex: 1` + `minWidth: 0` + `minHeight: 0` so the
 *   main column can shrink and scroll independently of its parent. The
 *   `minHeight: 0` is load-bearing when the layout sits inside another
 *   flex-column (which would otherwise collapse the row to content
 *   height).
 * - Main column: `flex: 1` + `minWidth: 0` + flex-column. Below
 *   `overlayBelowPx` margin-right stays 0 (panel overlays). At or
 *   above `overlayBelowPx` margin-right yields `panelWidth` when the
 *   panel is open.
 * - Directional transitions on `margin`: `easeOut + enteringScreen`
 *   when opening, `sharp + leavingScreen` when closing. Matches MUI's
 *   standard drawer motion.
 * - Panel is rendered as the row's second child without any wrapping
 *   so consumers can attach their own error boundary, portal, etc.
 */
function SidePanelLayout({
  mainContent,
  panel,
  isPanelOpen,
  panelWidth = SIDE_PANEL_DEFAULT_WIDTH,
  overlayBelowPx = SIDE_PANEL_DEFAULT_OVERLAY_BREAKPOINT_PX,
  rootTestId,
  mainColumnTestId,
}: SidePanelLayoutProps) {
  const theme = useTheme();

  return (
    <Box
      display="flex"
      flexDirection="row"
      width="100%"
      flex={1}
      minHeight={0}
      minWidth={0}
      data-testid={rootTestId}
    >
      <Box
        flex={1}
        minWidth={0}
        display="flex"
        flexDirection="column"
        data-testid={mainColumnTestId}
        sx={{
          marginRight: 0,
          // Push is gated by a raw `@media (min-width)` rule rather
          // than an MUI `sx` breakpoint key so it can sit at a
          // non-token value (e.g. 1100 px) without mutating the theme.
          // Must match the `SidePanel` overlay query exactly so the
          // two thresholds cannot drift.
          [`@media (min-width:${overlayBelowPx}px)`]: {
            marginRight: isPanelOpen ? `${panelWidth}px` : 0,
          },
          transition: theme.transitions.create('margin', {
            easing: isPanelOpen
              ? theme.transitions.easing.easeOut
              : theme.transitions.easing.sharp,
            duration: isPanelOpen
              ? theme.transitions.duration.enteringScreen
              : theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        {mainContent}
      </Box>
      {panel}
    </Box>
  );
}

export default SidePanelLayout;
